/**
 * The preferences screen's controller.
 *
 * What it owns that the model cannot: the loaded list, which rows have a write
 * in flight, and the one-at-a-time save. The model decides what a row says and
 * whether it carries a warning; the constants decide the words.
 */

import { useCallback, useEffect, useState } from "react";
import type {
  NotificationDelivery,
  NotificationDigestResponse,
  NotificationPreference,
  NotificationType,
} from "@ondo/shared";

import {
  fetchNotificationDigest,
  fetchNotificationPreferences,
  setNotificationDigestHour,
  setNotificationPreference,
} from "../lib/api.ts";
import {
  describeDigestError,
  describeError,
  describeLoadError,
  resolve,
  withDelivery,
} from "./notificationPreferences.model.ts";
import type { PreferencesView } from "./notificationPreferences.model.ts";

export interface PreferencesController {
  view: PreferencesView;
  /** Named for the intent, not the setter it wraps. */
  choose: (type: NotificationType, delivery: NotificationDelivery) => void;
  /** The same, for WHEN the digest arrives — OND-96. An hour 0-23, in UTC. */
  chooseHour: (hourUtc: number) => void;
}

/**
 * `principalOndoId` is whose preferences, and `canWrite` whether this reader may
 * change them.
 *
 * Both are decided by the workspace rather than here, exactly as the inbox's
 * are: an organisation's workspace configures the FIRM's inbox, and whether the
 * reader administers that firm is `atLeast(membership?.level, "admin")` — whose
 * null branch answers yes, which is what makes a personal workspace always
 * writable.
 *
 * `canWrite` GATES NOTHING THAT MATTERS, and must not be mistaken for the
 * authorization. `ondo.set_notification_preference()` refuses a firm the caller
 * does not administer whatever this says; this only decides whether the screen
 * offers a control that would be refused.
 */
export function useNotificationPreferencesController(input: {
  principalOndoId?: string | null;
  canWrite: boolean;
  isFirm: boolean;
}): PreferencesController {
  const { principalOndoId = null, canWrite, isFirm } = input;

  const [preferences, setPreferences] = useState<NotificationPreference[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /** Which kinds have a write in flight. Per kind, so one row's spinner is its own. */
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [digest, setDigest] = useState<NotificationDigestResponse | null>(null);
  const [digestSaving, setDigestSaving] = useState(false);

  useEffect(() => {
    let active = true;

    fetchNotificationPreferences(principalOndoId)
      .then((response) => {
        if (active) setPreferences(response.preferences);
      })
      .catch((error: unknown) => {
        if (active) setLoadError(describeLoadError(error));
      });

    // TWO INDEPENDENT READS, AND A FAILURE OF THIS ONE IS NOT A FAILURE OF THE
    // SCREEN. The hour is one section; the kinds are the rest of it. A settings
    // screen that refused to open because a time did not arrive would be worse
    // than one missing a section, so this failure is swallowed into an absent
    // control — the model's `digest: null`.
    fetchNotificationDigest(principalOndoId)
      .then((response) => {
        if (active) setDigest(response);
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [principalOndoId]);

  const choose = useCallback(
    (type: NotificationType, delivery: NotificationDelivery) => {
      // OPTIMISTIC, AND THEN CORRECTED FROM THE ANSWER. A radio that waited for
      // a round trip before moving reads as a broken control, and this write is
      // idempotent — the route is a PUT of one kind — so repeating or racing it
      // cannot leave two rows behind.
      setPreferences((current) =>
        current ? withDelivery(current, type, delivery) : current,
      );
      setWriteError(null);
      setSaved(false);
      setPending((current) => ({ ...current, [type]: true }));

      setNotificationPreference({ type, delivery }, principalOndoId)
        .then((written) => {
          // The server's answer, not the request — so a mode the database
          // normalised shows as what was actually stored rather than as what
          // was asked for.
          setPreferences((current) =>
            current ? withDelivery(current, written.type, written.delivery) : current,
          );
          setSaved(true);
        })
        .catch((error: unknown) => {
          setWriteError(describeError(error));
          // PUT BACK, which matters more here than on most screens: a control
          // left showing "off" after a refused write would tell somebody they
          // had silenced a remittance notice they are still being sent.
          void fetchNotificationPreferences(principalOndoId)
            .then((response) => setPreferences(response.preferences))
            .catch(() => undefined);
        })
        .finally(() => {
          setPending((current) => {
            const next = { ...current };
            delete next[type];
            return next;
          });
        });
    },
    [principalOndoId],
  );

  const chooseHour = useCallback(
    (hourUtc: number) => {
      // OPTIMISTIC, for `choose`'s reason — a select that waited for a round
      // trip before moving reads as a broken control — and `chosen` becomes
      // true at once, because it is true the moment they choose.
      setDigest((current) => (current ? { ...current, hourUtc, chosen: true } : current));
      setWriteError(null);
      setSaved(false);
      setDigestSaving(true);

      setNotificationDigestHour({ hourUtc }, principalOndoId)
        .then((written) => {
          // The server's answer, not the request, which is `choose`'s rule one
          // control along.
          setDigest(written);
          setSaved(true);
        })
        .catch((error: unknown) => {
          setWriteError(describeDigestError(error));
          // PUT BACK. A control left showing a time that was refused would tell
          // a firm's member they had moved the firm's digest when they had not.
          void fetchNotificationDigest(principalOndoId)
            .then((response) => setDigest(response))
            .catch(() => undefined);
        })
        .finally(() => setDigestSaving(false));
    },
    [principalOndoId],
  );

  return {
    view: resolve({
      preferences,
      pending,
      digest,
      digestSaving,
      loadError,
      writeError,
      saved,
      canWrite,
      isFirm,
    }),
    choose,
    chooseHour,
  };
}
